import { useState, useEffect, useCallback, useRef } from 'react'
import { competitorMapApi } from '@/lib/api/client'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import type {
  Competitor,
  CompetitorColumnSettings,
  CompetitorBrief,
  BriefScope,
  DiscoveryResult,
} from '@ispot/shared'
import { DEFAULT_VISIBLE_COLUMNS } from '@ispot/shared'

const ENRICHMENT_POLL_INTERVAL_MS = 4000
const ENRICHMENT_POLL_MAX_DURATION_MS = 5 * 60 * 1000

// Shallow per-row comparison: same length, same id ordering, identical values
// on the fields that drive the table. Lets background polls skip state updates
// when the server returned the same data, preventing needless table re-renders.
function competitorsShallowEqual(a: Competitor[], b: Competitor[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (x === y) continue
    if (
      x.id !== y.id ||
      x.name !== y.name ||
      x.category !== y.category ||
      x.type !== y.type ||
      x.url !== y.url ||
      x.pricing !== y.pricing ||
      x.whatTheyDo !== y.whatTheyDo ||
      x.strengths !== y.strengths ||
      x.gaps !== y.gaps ||
      x.notes !== y.notes ||
      x.investors !== y.investors ||
      x.hq !== y.hq ||
      x.isAiFirst !== y.isAiFirst ||
      x.suggestedByAi !== y.suggestedByAi ||
      x.enrichmentStatus !== y.enrichmentStatus ||
      x.enrichmentError !== y.enrichmentError ||
      x.updatedAt !== y.updatedAt
    ) {
      return false
    }
  }
  return true
}

interface CompetitorMapState {
  competitors: Competitor[]
  columnSettings: CompetitorColumnSettings | null
  activeBrief: CompetitorBrief | null
  briefScope: BriefScope | null
  isLoading: boolean
  isBriefLoading: boolean
  isDiscovering: boolean
  error: string | null
}

export function useCompetitorMap(accountId: string, projectId: string) {
  const [state, setState] = useState<CompetitorMapState>({
    competitors: [],
    columnSettings: null,
    activeBrief: null,
    briefScope: null,
    isLoading: false,
    isBriefLoading: false,
    isDiscovering: false,
    error: null,
  })

  // ============================================================================
  // LOAD
  // ============================================================================

  // Background refetches must not flip isLoading — that swaps the table for a
  // "Loading…" placeholder and reads to the user as a tab refresh.
  const loadCompetitors = useCallback(async (opts?: { background?: boolean }) => {
    const isBackground = opts?.background === true
    if (!isBackground) {
      setState(s => ({ ...s, isLoading: true, error: null }))
    }
    try {
      const [competitors, settings] = await Promise.all([
        competitorMapApi.listCompetitors(accountId, projectId) as Promise<Competitor[]>,
        competitorMapApi.getColumnSettings(accountId, projectId) as Promise<CompetitorColumnSettings | null>,
      ])
      setState(s => {
        // Avoid replacing references when nothing actually changed — keeps
        // table rows referentially stable so React doesn't reconcile them.
        const competitorsChanged = !competitorsShallowEqual(s.competitors, competitors)
        return {
          ...s,
          competitors: competitorsChanged ? competitors : s.competitors,
          columnSettings: settings ?? s.columnSettings ?? {
            id: '',
            projectId,
            accountId,
            visibleColumns: DEFAULT_VISIBLE_COLUMNS as Record<string, boolean>,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          isLoading: false,
        }
      })
    } catch (err) {
      setState(s => ({
        ...s,
        isLoading: isBackground ? s.isLoading : false,
        error: isBackground ? s.error : 'Failed to load competitor map',
      }))
    }
  }, [accountId, projectId])

  useEffect(() => {
    if (accountId && projectId) {
      loadCompetitors()
    }
  }, [accountId, projectId, loadCompetitors])

  // Auto-refresh on any Competitor create/update/delete from external sources
  // (e.g. Skerpa actions). Use background mode so the table doesn't flicker.
  useEntityCrudListener(
    'Competitor',
    useCallback(() => loadCompetitors({ background: true }), [loadCompetitors]),
  )

  // Poll while any competitor is actively being enriched.
  // Drive the effect off a stable signature (sorted IDs of enriching rows)
  // so it doesn't tear down on every state update.
  const enrichingSignature = state.competitors
    .filter(c => c.enrichmentStatus === 'enriching')
    .map(c => c.id)
    .sort()
    .join(',')
  const pollStartRef = useRef<number | null>(null)
  useEffect(() => {
    if (!enrichingSignature) {
      pollStartRef.current = null
      return
    }
    if (pollStartRef.current === null) pollStartRef.current = Date.now()

    const interval = setInterval(() => {
      const elapsed = Date.now() - (pollStartRef.current ?? 0)
      if (elapsed > ENRICHMENT_POLL_MAX_DURATION_MS) {
        clearInterval(interval)
        return
      }
      loadCompetitors({ background: true })
    }, ENRICHMENT_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [enrichingSignature, loadCompetitors])

  // ============================================================================
  // COMPETITOR MUTATIONS
  // ============================================================================

  const createCompetitor = useCallback(async (data: Partial<Competitor>, apiKey?: string) => {
    const body: Record<string, unknown> = { ...data }
    if (apiKey) body.apiKey = apiKey
    const competitor = await competitorMapApi.createCompetitor(accountId, projectId, body) as Competitor
    setState(s => ({ ...s, competitors: [...s.competitors, competitor] }))
    return competitor
  }, [accountId, projectId])

  const enrichCompetitor = useCallback(async (id: string, apiKey: string) => {
    // Optimistically mark as enriching so UI reflects state immediately
    setState(s => ({
      ...s,
      competitors: s.competitors.map(c =>
        c.id === id ? { ...c, enrichmentStatus: 'enriching' as const, enrichmentError: undefined } : c,
      ),
    }))
    try {
      await competitorMapApi.enrichCompetitor(accountId, projectId, id, apiKey)
    } catch (err) {
      // Revert optimistic state on failure
      await loadCompetitors()
      throw err
    }
  }, [accountId, projectId, loadCompetitors])

  const updateCompetitor = useCallback(async (id: string, data: Partial<Competitor>) => {
    // Optimistic update
    setState(s => ({
      ...s,
      competitors: s.competitors.map(c => c.id === id ? { ...c, ...data } : c),
    }))
    try {
      const updated = await competitorMapApi.updateCompetitor(accountId, projectId, id, data as Record<string, unknown>) as Competitor
      setState(s => ({
        ...s,
        competitors: s.competitors.map(c => c.id === id ? updated : c),
      }))
      return updated
    } catch (err) {
      // Revert on error
      await loadCompetitors()
      throw err
    }
  }, [accountId, projectId, loadCompetitors])

  const deleteCompetitor = useCallback(async (id: string) => {
    setState(s => ({ ...s, competitors: s.competitors.filter(c => c.id !== id) }))
    try {
      await competitorMapApi.deleteCompetitor(accountId, projectId, id)
    } catch (err) {
      await loadCompetitors()
      throw err
    }
  }, [accountId, projectId, loadCompetitors])

  // ============================================================================
  // COLUMN SETTINGS
  // ============================================================================

  const updateColumnSettings = useCallback(async (visibleColumns: Record<string, boolean>) => {
    setState(s => ({
      ...s,
      columnSettings: s.columnSettings ? { ...s.columnSettings, visibleColumns } : null,
    }))
    const settings = await competitorMapApi.updateColumnSettings(accountId, projectId, { visibleColumns }) as CompetitorColumnSettings
    setState(s => ({ ...s, columnSettings: settings }))
  }, [accountId, projectId])

  // ============================================================================
  // BRIEFS
  // ============================================================================

  const openBrief = useCallback(async (scope: BriefScope) => {
    setState(s => ({ ...s, briefScope: scope, activeBrief: null }))
    try {
      let brief: CompetitorBrief | null = null
      if (scope.type === 'competitor') {
        brief = await competitorMapApi.getBriefByCompetitor(accountId, projectId, scope.competitorId) as CompetitorBrief | null
      } else {
        brief = await competitorMapApi.getBriefByCategory(accountId, projectId, scope.category) as CompetitorBrief | null
      }
      setState(s => ({ ...s, activeBrief: brief }))
    } catch {
      // Brief may not exist yet — that's OK
      setState(s => ({ ...s, activeBrief: null }))
    }
  }, [accountId, projectId])

  const closeBrief = useCallback(() => {
    setState(s => ({ ...s, activeBrief: null, briefScope: null }))
  }, [])

  const generateBrief = useCallback(async (scope: BriefScope, apiKey: string) => {
    setState(s => ({ ...s, isBriefLoading: true, briefScope: scope }))
    try {
      const scopeData = scope.type === 'competitor'
        ? { scope: 'competitor' as const, competitorId: scope.competitorId, apiKey }
        : { scope: 'category' as const, category: scope.category, apiKey }

      const brief = await competitorMapApi.generateBrief(accountId, projectId, scopeData) as CompetitorBrief
      setState(s => ({ ...s, activeBrief: brief, isBriefLoading: false }))
      return brief
    } catch (err) {
      setState(s => ({ ...s, isBriefLoading: false }))
      throw err
    }
  }, [accountId, projectId])

  // Background enrichment — fires the server-side enrichment pipeline for this competitor.
  // The server updates status/fields as the pipeline progresses; polling refreshes the UI.
  const enrichCompetitorBackground = useCallback((competitorId: string, apiKey: string) => {
    competitorMapApi.enrichCompetitor(accountId, projectId, competitorId, apiKey).catch(() => {
      // Silently fail — user can retry via the Re-enrich button
    })
  }, [accountId, projectId])

  const discoverCompetitors = useCallback(async (apiKey: string): Promise<DiscoveryResult | null> => {
    setState(s => ({ ...s, isDiscovering: true, error: null }))
    try {
      const result = await competitorMapApi.discoverCompetitors(accountId, projectId, { apiKey }) as DiscoveryResult
      await loadCompetitors()
      setState(s => ({ ...s, isDiscovering: false }))
      return result
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Discovery failed'
      setState(s => ({ ...s, isDiscovering: false, error: msg }))
      return null
    }
  }, [accountId, projectId, loadCompetitors])

  return {
    ...state,
    loadCompetitors,
    createCompetitor,
    updateCompetitor,
    deleteCompetitor,
    updateColumnSettings,
    openBrief,
    closeBrief,
    generateBrief,
    enrichCompetitor,
    enrichCompetitorBackground,
    discoverCompetitors,
  }
}
